/**
 * CooldownButton Component
 *
 * Button with built-in cooldown timer for rate-limited actions.
 * Implements UX Intent pattern: friction_intent: intentional
 *
 * Features:
 * - Visual countdown timer
 * - Auto-disable during cooldown
 * - Persistent cooldown across page refreshes (optional)
 * - Accessible countdown announcements
 *
 * Used by: SCR-803 (Forgot Password - 60s resend cooldown),
 *          SCR-804 (Change Password - rate limit indicator)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ComponentProps } from 'react';

import { cn } from '../../utils/cn';
import { Button } from '../Button';

export interface CooldownButtonProps extends Omit<ComponentProps<typeof Button>, 'disabled'> {
  /** Cooldown duration in seconds */
  cooldownSeconds: number;
  /** Callback when cooldown completes */
  onCooldownComplete?: () => void;
  /** Whether to start in cooldown state */
  startInCooldown?: boolean;
  /** Persist cooldown in localStorage (provide unique key) */
  persistKey?: string;
  /** Custom cooldown text format. Use {seconds} as placeholder */
  cooldownText?: string;
  /** Text to show when action is available */
  availableText?: string;
  /** Whether button is disabled (separate from cooldown) */
  disabled?: boolean;
  /** Show progress ring during cooldown */
  showProgress?: boolean;
}

export function CooldownButton({
  cooldownSeconds,
  onCooldownComplete,
  startInCooldown = false,
  persistKey,
  cooldownText = 'Wacht {seconds}s',
  availableText,
  disabled = false,
  showProgress = false,
  onClick,
  children,
  className,
  ...props
}: CooldownButtonProps) {
  const [remainingSeconds, setRemainingSeconds] = useState(() => {
    // Check for persisted cooldown
    if (persistKey) {
      const stored = localStorage.getItem(`cooldown:${persistKey}`);
      if (stored) {
        const endTime = parseInt(stored, 10);
        const remaining = Math.ceil((endTime - Date.now()) / 1000);
        if (remaining > 0) return remaining;
        localStorage.removeItem(`cooldown:${persistKey}`);
      }
    }
    return startInCooldown ? cooldownSeconds : 0;
  });

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCoolingDown = remainingSeconds > 0;

  // Countdown effect
  useEffect(() => {
    if (remainingSeconds > 0) {
      intervalRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (persistKey) {
              localStorage.removeItem(`cooldown:${persistKey}`);
            }
            onCooldownComplete?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [remainingSeconds > 0, persistKey, onCooldownComplete]);

  // Start cooldown
  const startCooldown = useCallback(() => {
    const endTime = Date.now() + cooldownSeconds * 1000;
    if (persistKey) {
      localStorage.setItem(`cooldown:${persistKey}`, endTime.toString());
    }
    setRemainingSeconds(cooldownSeconds);
  }, [cooldownSeconds, persistKey]);

  // Handle click
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isCoolingDown || disabled) return;

      onClick?.(e);
      startCooldown();
    },
    [isCoolingDown, disabled, onClick, startCooldown]
  );

  // Format button text
  const buttonText = isCoolingDown
    ? cooldownText.replace('{seconds}', remainingSeconds.toString())
    : availableText || children;

  // Calculate progress for visual indicator
  const progress = isCoolingDown ? (remainingSeconds / cooldownSeconds) * 100 : 0;

  return (
    <Button
      {...props}
      onClick={handleClick}
      disabled={isCoolingDown || disabled}
      className={cn('relative overflow-hidden', className)}
      aria-disabled={isCoolingDown || disabled}
      aria-live="polite"
    >
      {/* Progress background */}
      {showProgress && isCoolingDown && (
        <span
          className="absolute inset-0 bg-mv-gray-200/50 transition-transform duration-1000 ease-linear"
          style={{ transform: `translateX(${100 - progress}%)` }}
          aria-hidden="true"
        />
      )}

      {/* Button content */}
      <span className="relative">{buttonText}</span>
    </Button>
  );
}

/**
 * Hook for managing cooldown state externally
 */
export interface UseCooldownOptions {
  /** Cooldown duration in seconds */
  seconds: number;
  /** Persist in localStorage with this key */
  persistKey?: string;
  /** Callback when cooldown completes */
  onComplete?: () => void;
}

export interface UseCooldownResult {
  /** Remaining seconds in cooldown */
  remaining: number;
  /** Whether currently in cooldown */
  isCoolingDown: boolean;
  /** Start the cooldown */
  start: () => void;
  /** Reset the cooldown */
  reset: () => void;
  /** Progress percentage (0-100) */
  progress: number;
}

export function useCooldown({
  seconds,
  persistKey,
  onComplete,
}: UseCooldownOptions): UseCooldownResult {
  const [remaining, setRemaining] = useState(() => {
    if (persistKey) {
      const stored = localStorage.getItem(`cooldown:${persistKey}`);
      if (stored) {
        const endTime = parseInt(stored, 10);
        const rem = Math.ceil((endTime - Date.now()) / 1000);
        if (rem > 0) return rem;
        localStorage.removeItem(`cooldown:${persistKey}`);
      }
    }
    return 0;
  });

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (remaining > 0) {
      intervalRef.current = setInterval(() => {
        setRemaining((prev) => {
          if (prev <= 1) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (persistKey) {
              localStorage.removeItem(`cooldown:${persistKey}`);
            }
            onComplete?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [remaining > 0, persistKey, onComplete]);

  const start = useCallback(() => {
    const endTime = Date.now() + seconds * 1000;
    if (persistKey) {
      localStorage.setItem(`cooldown:${persistKey}`, endTime.toString());
    }
    setRemaining(seconds);
  }, [seconds, persistKey]);

  const reset = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (persistKey) {
      localStorage.removeItem(`cooldown:${persistKey}`);
    }
    setRemaining(0);
  }, [persistKey]);

  return {
    remaining,
    isCoolingDown: remaining > 0,
    start,
    reset,
    progress: remaining > 0 ? (remaining / seconds) * 100 : 0,
  };
}

/**
 * CountdownConfirm Component
 *
 * Shows a countdown before executing an action.
 * Used for "logout all sessions" type operations.
 */
export interface CountdownConfirmProps {
  /** Countdown duration in seconds */
  seconds: number;
  /** Called when countdown completes */
  onComplete: () => void;
  /** Called when user cancels */
  onCancel: () => void;
  /** Message to display. Use {seconds} as placeholder */
  message?: string;
  /** Cancel button label */
  cancelLabel?: string;
  /** Whether to auto-start on mount */
  autoStart?: boolean;
  /** Custom class name */
  className?: string;
}

export function CountdownConfirm({
  seconds,
  onComplete,
  onCancel,
  message = 'Actie wordt uitgevoerd over {seconds} seconden...',
  cancelLabel = 'Annuleren',
  autoStart = true,
  className,
}: CountdownConfirmProps) {
  const [remaining, setRemaining] = useState(autoStart ? seconds : 0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (autoStart && remaining > 0) {
      intervalRef.current = setInterval(() => {
        setRemaining((prev) => {
          if (prev <= 1) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            onComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoStart, onComplete]);

  const handleCancel = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setRemaining(0);
    onCancel();
  };

  if (remaining === 0 && !autoStart) return null;

  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-lg bg-amber-50 border border-amber-200 p-4',
        className
      )}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-center gap-3">
        {/* Countdown circle */}
        <div className="relative h-10 w-10">
          <svg className="h-10 w-10 -rotate-90" viewBox="0 0 36 36">
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              className="stroke-amber-200"
              strokeWidth="3"
            />
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              className="stroke-amber-500 transition-all duration-1000 ease-linear"
              strokeWidth="3"
              strokeDasharray={`${(remaining / seconds) * 100} 100`}
              strokeLinecap="round"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-amber-700">
            {remaining}
          </span>
        </div>

        <span className="text-sm text-amber-800">
          {message.replace('{seconds}', remaining.toString())}
        </span>
      </div>

      <Button variant="outline" size="sm" onClick={handleCancel}>
        {cancelLabel}
      </Button>
    </div>
  );
}
